import {
  AcceptInvitationException,
  AcceptInvitationExceptionData,
  AlreadyRegisteredException,
  AuthenticationException,
  AuthenticationFailedException,
  ClientException,
  ClientExceptionTypes,
  ClientRateLimitException,
  ClientRateLimitExceptionData,
  ClientValidationException,
  ConfirmationRequired,
  DeprecatedClientAuthException,
  EmailAliasException,
  ForbiddenException,
  ImmovableItineraryEvents,
  InvalidInviteException,
  InvalidTokenException,
  InvalidVerifyCodeException,
  ItineraryEventOutsideTimeFrame,
  ItineraryEventsPreventFlexibleTrip,
  OAuthProviderException,
  OutOfRangeBooking,
  OutOfRangeBookingConfirmationRequired,
  OutOfRangeBookingConfirmationRequiredData,
  OutOfRangeBookingData,
  OutOfRangeBookingSetDatesConfirmationRequired,
  ProviderMismatchException,
  SendInvitationException,
  SendInvitationExceptionData,
  SignInFailedException,
  StaleHotelRoomRecoveryRequired,
  ThatchAuthenticationException,
  ThatchEmailMismatchException,
  ThatchVerificationFailedException,
  UnavailableShareException,
  UnavailableShareExceptionData,
  UndeletableItineraryEvents,
  UnknownError,
  VerifyCodeAlreadyRedeemedException,
  VerifyCodeExpiredException,
  VerifyCodeNotFoundException,
  VerifyCodeNotRedeemedException,
  VerifyCodeSignInExpiredException,
  WrappedPromiseRejectionError,
} from "@/types/client-exceptions"
import {
  FlightDateOutOfRangeException,
  FlightExpirationException,
  FlightOfferUnavailableException,
  FlightPassengersChangedException,
  FlightSeatMapUnavailableException,
  FlightUnableToCheckoutException,
  FlightUnableToPriceException,
} from "@/types/flights/exceptions"
import { SimpleHttpError } from "@/types/http"
import { LoginAuthProvider } from "@/types/user"
import axios, { AxiosError } from "axios"

import { AxiosLightError } from "@/lib/axios-utils"

type ErrorObject = {
  __error_type: string
} & Record<string, unknown>
type ClientExceptionMap = {
  [K in ClientExceptionTypes]: (unparsed: ErrorObject) => Error
}
const buildMessage = (unparsed: ErrorObject) => {
  return "message" in unparsed && typeof unparsed.message === "string"
    ? unparsed.message
    : `Unknown ${unparsed.__error_type} occurred`
}

// i'm building it like this because i want to make sure its exhaustive that all the types are handled
// typescript will throw an error if we don't handle a type
export const clientExceptionMap: ClientExceptionMap = {
  ClientException: (unparsed: ErrorObject) =>
    new ClientException(
      "ClientException",
      buildMessage(unparsed),
      unparsed.data,
      { cause: unparsed }
    ),
  ClientValidationException: (unparsed: ErrorObject) =>
    new ClientValidationException(buildMessage(unparsed), unparsed.data, {
      cause: unparsed,
    }),
  ClientRateLimitException: (unparsed: ErrorObject) =>
    new ClientRateLimitException(
      buildMessage(unparsed),
      unparsed.data as ClientRateLimitExceptionData,
      { cause: unparsed }
    ),
  AuthenticationException: (unparsed: ErrorObject) =>
    new AuthenticationException(buildMessage(unparsed), { cause: unparsed }),
  ForbiddenException: (unparsed: ErrorObject) =>
    new ForbiddenException(buildMessage(unparsed), { cause: unparsed }),
  ProviderMismatchException: (unparsed: ErrorObject) =>
    new ProviderMismatchException(
      buildMessage(unparsed),
      unparsed.data as {
        attempted: LoginAuthProvider
        valid_providers: LoginAuthProvider[]
      },
      { cause: unparsed }
    ),
  ImmovableItineraryEvents: (unparsed: ErrorObject) =>
    new ImmovableItineraryEvents(
      buildMessage(unparsed),
      unparsed.data as number[],
      { cause: unparsed }
    ),
  ConfirmationRequired: (unparsed: ErrorObject) =>
    new ConfirmationRequired(
      buildMessage(unparsed),
      unparsed.data as {
        movable_event_ids: number[]
        extendable_event_ids: number[]
      },
      { cause: unparsed }
    ),
  ItineraryEventsPreventFlexibleTrip: (unparsed: ErrorObject) =>
    new ItineraryEventsPreventFlexibleTrip(
      buildMessage(unparsed),
      unparsed.data as number[]
    ),
  UndeletableItineraryEvents: (unparsed: ErrorObject) =>
    new UndeletableItineraryEvents(
      buildMessage(unparsed),
      unparsed.data as number[]
    ),
  ItineraryEventOutsideTimeFrame: (unparsed: ErrorObject) =>
    new ItineraryEventOutsideTimeFrame(
      buildMessage(unparsed),
      unparsed.data as number[]
    ),
  OutOfRangeBooking: (unparsed: ErrorObject) =>
    new OutOfRangeBooking(
      buildMessage(unparsed),
      unparsed.data as OutOfRangeBookingData | undefined,
      { cause: unparsed }
    ),
  OutOfRangeBookingConfirmationRequired: (unparsed: ErrorObject) =>
    new OutOfRangeBookingConfirmationRequired(
      buildMessage(unparsed),
      unparsed.data as OutOfRangeBookingConfirmationRequiredData | undefined,
      { cause: unparsed }
    ),
  OutOfRangeBookingSetDatesConfirmationRequired: (unparsed: ErrorObject) =>
    new OutOfRangeBookingSetDatesConfirmationRequired(
      buildMessage(unparsed),
      unparsed.data as OutOfRangeBookingConfirmationRequiredData | undefined,
      { cause: unparsed }
    ),
  StaleHotelRoomRecoveryRequired: (unparsed: ErrorObject) =>
    new StaleHotelRoomRecoveryRequired(
      buildMessage(unparsed),
      unparsed.data as { reason?: string } | undefined,
      { cause: unparsed }
    ),
  InvalidInviteException: (unparsed: ErrorObject) =>
    new InvalidInviteException(buildMessage(unparsed), { cause: unparsed }),
  UnavailableShareException: (unparsed: ErrorObject) =>
    new UnavailableShareException(
      buildMessage(unparsed),
      unparsed.data as UnavailableShareExceptionData
    ),
  AcceptInvitationException: (unparsed: ErrorObject) =>
    new AcceptInvitationException(
      buildMessage(unparsed),
      unparsed.data as AcceptInvitationExceptionData
    ),
  SendInvitationException: (unparsed: ErrorObject) =>
    new SendInvitationException(
      buildMessage(unparsed),
      unparsed.data as SendInvitationExceptionData
    ),
  ThatchEmailMismatchException: (unparsed: ErrorObject) =>
    new ThatchEmailMismatchException(buildMessage(unparsed)),
  ClientAuthException: (unparsed: ErrorObject) =>
    new DeprecatedClientAuthException(buildMessage(unparsed)),
  FlightSeatMapUnavailableException: (unparsed: ErrorObject) =>
    new FlightSeatMapUnavailableException(buildMessage(unparsed)),
  FlightDateOutOfRangeException: (unparsed: ErrorObject) =>
    new FlightDateOutOfRangeException(buildMessage(unparsed)),
  FlightExpirationException: (unparsed: ErrorObject) =>
    new FlightExpirationException(buildMessage(unparsed)),
  FlightOfferUnavailableException: (unparsed: ErrorObject) =>
    new FlightOfferUnavailableException(buildMessage(unparsed), unparsed.data),
  FlightPassengersChangedException: (unparsed: ErrorObject) =>
    new FlightPassengersChangedException(buildMessage(unparsed), unparsed.data),
  FlightUnableToPriceException: (unparsed: ErrorObject) =>
    new FlightUnableToPriceException(buildMessage(unparsed)),
  FlightUnableToCheckoutException: (unparsed: ErrorObject) =>
    new FlightUnableToCheckoutException(buildMessage(unparsed)),
  InvalidVerifyCodeException: (unparsed: ErrorObject) =>
    new InvalidVerifyCodeException(buildMessage(unparsed)),
  VerifyCodeExpiredException: (unparsed: ErrorObject) =>
    new VerifyCodeExpiredException(buildMessage(unparsed), { cause: unparsed }),
  VerifyCodeAlreadyRedeemedException: (unparsed: ErrorObject) =>
    new VerifyCodeAlreadyRedeemedException(buildMessage(unparsed), {
      cause: unparsed,
    }),
  VerifyCodeNotFoundException: (unparsed: ErrorObject) =>
    new VerifyCodeNotFoundException(buildMessage(unparsed), {
      cause: unparsed,
    }),
  VerifyCodeSignInExpiredException: (unparsed: ErrorObject) =>
    new VerifyCodeSignInExpiredException(buildMessage(unparsed), {
      cause: unparsed,
    }),
  VerifyCodeNotRedeemedException: (unparsed: ErrorObject) =>
    new VerifyCodeNotRedeemedException(buildMessage(unparsed), {
      cause: unparsed,
    }),
  AuthenticationFailedException: (unparsed: ErrorObject) =>
    new AuthenticationFailedException(buildMessage(unparsed), {
      cause: unparsed,
    }),
  SignInFailedException: (unparsed: ErrorObject) =>
    new SignInFailedException(buildMessage(unparsed), { cause: unparsed }),
  AlreadyRegisteredException: (unparsed: ErrorObject) =>
    new AlreadyRegisteredException(buildMessage(unparsed), { cause: unparsed }),
  InvalidTokenException: (unparsed: ErrorObject) =>
    new InvalidTokenException(buildMessage(unparsed), { cause: unparsed }),
  OAuthProviderException: (unparsed: ErrorObject) =>
    new OAuthProviderException(buildMessage(unparsed), { cause: unparsed }),
  ThatchVerificationFailedException: (unparsed: ErrorObject) =>
    new ThatchVerificationFailedException(buildMessage(unparsed), {
      cause: unparsed,
    }),
  ThatchAuthenticationException: (unparsed: ErrorObject) =>
    new ThatchAuthenticationException(buildMessage(unparsed), {
      cause: unparsed,
    }),
  EmailAliasException: (unparsed: ErrorObject) =>
    new EmailAliasException(buildMessage(unparsed), { cause: unparsed }),
}

export interface NormalizeServerErrorOptions {
  requestPath?: string
}

// useful for normalizing serialized errors from the server or redux unwraps
export function normalizeServerError(
  e: unknown,
  options?: NormalizeServerErrorOptions
): Error {
  // these errors may have a data object we need to process
  const isForProcessError =
    e instanceof SimpleHttpError ||
    e instanceof AxiosError ||
    e instanceof AxiosLightError ||
    e instanceof WrappedPromiseRejectionError

  if (e instanceof Error && !isForProcessError) {
    return e
  }

  // First convert AxiosError or AxiosLightError to SimpleHttpError
  let simpleError: SimpleHttpError | null = null

  if (e instanceof AxiosLightError) {
    simpleError = new SimpleHttpError(e.message, {
      response: {
        type: "SimpleHttpResponse",
        status: e.response?.status,
        statusText: e.response?.statusText,
        data: e.response?.data,
      },
      requestPath: options?.requestPath,
      requestMethod: e.request?.method,
      isCancelled: e.isCancelled,
    })
  } else if (e instanceof AxiosError) {
    const response = e.response
      ? {
          type: "SimpleHttpResponse" as const,
          status: e.response.status,
          statusText: e.response.statusText,
          data: e.response.data,
        }
      : undefined

    simpleError = new SimpleHttpError(e.message, {
      response,
      requestPath: options?.requestPath,
      requestMethod: e.request?.method,
      isCancelled: axios.isCancel(e),
    })
  }

  // If we converted to SimpleHttpError, use that for further processing
  if (simpleError) {
    e = simpleError
  }

  // upgrade SimpleHttpError to AuthenticationException or
  if (e instanceof SimpleHttpError && e.response?.status) {
    const status = e.response.status
    const data = e.response.data

    const needsErrorType =
      data === null ||
      (typeof data === "object" &&
        (!("__error_type" in data) || typeof data.__error_type !== "string"))

    if (needsErrorType) {
      if (status === 401) {
        e.response.data = {
          __error_type: "AuthenticationException",
          message: e.message || "Authentication required",
        }
      } else if (status === 403) {
        e.response.data = {
          __error_type: "ForbiddenException",
          message: e.message || "Access forbidden",
        }
      }
    }
  }

  let unparsed: unknown | undefined = undefined
  if (e instanceof SimpleHttpError) {
    unparsed = e.response?.data
  } else if (e != null) {
    unparsed = e
  }

  if (unparsed instanceof WrappedPromiseRejectionError) {
    unparsed = unparsed.cause
  }

  const isErrorObject = (errorData: unknown): errorData is ErrorObject =>
    errorData != null &&
    typeof errorData === "object" &&
    "__error_type" in errorData &&
    typeof errorData.__error_type === "string"

  if (isErrorObject(unparsed)) {
    try {
      const mapper =
        clientExceptionMap[unparsed.__error_type as ClientExceptionTypes]
      if (mapper) {
        const mappedError = mapper(unparsed)
        // set the cause to the original error, important for SimpleHttpError as Honeybadger will use it for messaging
        mappedError.cause = e
        return mappedError
      }
    } catch (e) {
      console.error("Error building real error", e)
    }
  }

  // if we get to here and still have a simple error, return it
  if (e instanceof SimpleHttpError) {
    return e
  }

  return new UnknownError(
    typeof e === "string" ? e : "Unknown error occurred",
    e,
    { cause: e }
  )
}

export function httpIsCancelled(e: unknown): boolean {
  if (e instanceof SimpleHttpError) {
    return e.isCancelled ?? false
  }

  if (
    typeof e === "object" &&
    e !== null &&
    "type" in e &&
    e.type === "SimpleHttpError" &&
    "isCancelled" in e &&
    e.isCancelled === true
  ) {
    return true
  }

  if (
    typeof e === "object" &&
    e !== null &&
    "name" in e &&
    e.name === "AbortError"
  ) {
    return true
  }

  return axios.isCancel(e)
}

export function errorMessage(
  error: unknown,
  defaultMessage = "unknown error"
): string {
  if (error instanceof SimpleHttpError && error.message) {
    return error.message
  }

  if (typeof error === "string") {
    return error
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  if (error && typeof error === "object") {
    // Check for message or payload properties (payload is used by redux)
    for (const prop of ["message", "payload"]) {
      const value = (error as Record<string, unknown>)[prop]
      if (value && typeof value === "string") {
        return value
      }
    }
  }

  return defaultMessage
}
